import { useQuery } from '@tanstack/react-query';
import {
  Users,
  Baby,
  Heart,
  Calendar,
  TrendingUp,
  Activity,
  Bot,
  MessageSquare,
  PieChart as PieChartIcon,
  Clock,
  Sparkles,
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';

const supabase = getSupabaseAdminClient();

// Category colors for AI analytics
const CATEGORY_COLORS: Record<string, string> = {
  symptome: '#f43f5e',
  ernaehrung: '#22c55e',
  termine: '#3b82f6',
  baby_entwicklung: '#a855f7',
  behoerden: '#64748b',
  emotionen: '#ec4899',
  partner: '#f59e0b',
  geburt: '#14b8a6',
  sonstiges: '#6b7280',
};

const CATEGORY_LABELS: Record<string, string> = {
  symptome: 'Symptome',
  ernaehrung: 'Ernährung',
  termine: 'Termine',
  baby_entwicklung: 'Baby-Entwicklung',
  behoerden: 'Behörden',
  emotionen: 'Emotionen',
  partner: 'Partnerschaft',
  geburt: 'Geburt',
  sonstiges: 'Sonstiges',
};

export function AnalyticsPage() {
  // Fetch overall stats
  const { data: overallStats, isLoading: loadingStats } = useQuery({
    queryKey: ['analytics-overall'],
    queryFn: async () => {
      const [
        usersResult,
        pregnanciesResult,
        partnersResult,
        appointmentsResult,
        checklistsResult,
        swipesResult,
      ] = await Promise.all([
        supabase.from('profiles').select('id', { count: 'exact', head: true }),
        supabase.from('pregnancies').select('id', { count: 'exact', head: true }).eq('is_active', true),
        supabase.from('partner_connections').select('id', { count: 'exact', head: true }).eq('status', 'active'),
        supabase.from('user_appointments').select('id', { count: 'exact', head: true }),
        supabase.from('user_checklists').select('id', { count: 'exact', head: true }),
        supabase.from('name_swipes').select('id', { count: 'exact', head: true }),
      ]);

      return {
        users: usersResult.count || 0,
        pregnancies: pregnanciesResult.count || 0,
        partners: partnersResult.count || 0,
        appointments: appointmentsResult.count || 0,
        checklists: checklistsResult.count || 0,
        swipes: swipesResult.count || 0,
      };
    },
  });

  // Fetch user registrations over time (last 30 days)
  const { data: registrationData, isLoading: loadingRegistrations } = useQuery({
    queryKey: ['analytics-registrations'],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('profiles')
        .select('created_at')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;

      // Group by day
      const dayMap: Record<string, number> = {};
      (data as { created_at: string }[])?.forEach(profile => {
        const day = new Date(profile.created_at).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
        });
        dayMap[day] = (dayMap[day] || 0) + 1;
      });

      // Fill in missing days
      const result = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const day = date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
        });
        result.push({
          date: day,
          users: dayMap[day] || 0,
        });
      }

      return result;
    },
  });

  // Fetch AI usage stats
  const { data: aiUsageData, isLoading: loadingAI } = useQuery({
    queryKey: ['analytics-ai-usage'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_usage')
        .select('month, questions_count, tokens_used')
        .order('month', { ascending: true })
        .limit(12);

      if (error) throw error;

      return (data as { month: string; questions_count: number; tokens_used: number }[])?.map(d => ({
        month: d.month,
        questions: d.questions_count,
        tokens: d.tokens_used,
      })) || [];
    },
  });

  // Fetch activity stats (last 7 days)
  const { data: activityData, isLoading: loadingActivity } = useQuery({
    queryKey: ['analytics-activity'],
    queryFn: async () => {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

      const { data, error } = await supabase
        .from('activity_log')
        .select('action, created_at')
        .gte('created_at', sevenDaysAgo.toISOString());

      if (error) throw error;

      // Count by action
      const actionMap: Record<string, number> = {};
      (data as { action: string; created_at: string }[])?.forEach(log => {
        actionMap[log.action] = (actionMap[log.action] || 0) + 1;
      });

      return Object.entries(actionMap)
        .map(([action, count]) => ({ action, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);
    },
  });

  // Fetch daily active users (last 30 days)
  const { data: dauData, isLoading: loadingDAU } = useQuery({
    queryKey: ['analytics-dau'],
    queryFn: async () => {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data, error } = await supabase
        .from('activity_log')
        .select('user_id, created_at')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (error) throw error;

      // Count unique users per day
      const dayMap: Record<string, Set<string>> = {};
      (data as { user_id: string | null; created_at: string }[])?.forEach(log => {
        if (!log.user_id) return;
        const day = new Date(log.created_at).toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
        });
        if (!dayMap[day]) {
          dayMap[day] = new Set();
        }
        dayMap[day].add(log.user_id);
      });

      // Fill in missing days
      const result = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const day = date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
        });
        result.push({
          date: day,
          dau: dayMap[day]?.size || 0,
        });
      }

      return result;
    },
  });

  // Fetch AI category distribution
  const { data: aiCategoryData, isLoading: loadingAICategories } = useQuery({
    queryKey: ['analytics-ai-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('category')
        .eq('role', 'user')
        .not('category', 'is', null);

      if (error) throw error;

      // Count by category
      const categoryMap: Record<string, number> = {};
      (data as { category: string }[])?.forEach(msg => {
        if (msg.category) {
          categoryMap[msg.category] = (categoryMap[msg.category] || 0) + 1;
        }
      });

      return Object.entries(categoryMap)
        .map(([category, count]) => ({
          category,
          label: CATEGORY_LABELS[category] || category,
          count,
          color: CATEGORY_COLORS[category] || '#6b7280',
        }))
        .sort((a, b) => b.count - a.count);
    },
  });

  // Fetch AI usage by SSW
  const { data: aiBySSWData, isLoading: loadingAIBySSW } = useQuery({
    queryKey: ['analytics-ai-by-ssw'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('ssw_at_message')
        .eq('role', 'user')
        .not('ssw_at_message', 'is', null);

      if (error) throw error;

      // Count by SSW
      const sswMap: Record<number, number> = {};
      (data as { ssw_at_message: number }[])?.forEach(msg => {
        if (msg.ssw_at_message) {
          sswMap[msg.ssw_at_message] = (sswMap[msg.ssw_at_message] || 0) + 1;
        }
      });

      // Create array for all SSWs 1-42
      const result = [];
      for (let ssw = 1; ssw <= 42; ssw++) {
        result.push({
          ssw,
          count: sswMap[ssw] || 0,
        });
      }

      return result;
    },
  });

  // Fetch AI usage by hour
  const { data: aiByHourData, isLoading: loadingAIByHour } = useQuery({
    queryKey: ['analytics-ai-by-hour'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('created_at')
        .eq('role', 'user');

      if (error) throw error;

      // Count by hour
      const hourMap: Record<number, number> = {};
      (data as { created_at: string }[])?.forEach(msg => {
        const hour = new Date(msg.created_at).getHours();
        hourMap[hour] = (hourMap[hour] || 0) + 1;
      });

      // Create array for all hours 0-23
      const result = [];
      for (let hour = 0; hour < 24; hour++) {
        result.push({
          hour: `${hour.toString().padStart(2, '0')}:00`,
          count: hourMap[hour] || 0,
        });
      }

      return result;
    },
  });

  // Fetch average response time
  const { data: avgResponseTime, isLoading: loadingResponseTime } = useQuery({
    queryKey: ['analytics-ai-response-time'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ai_messages')
        .select('response_time_ms')
        .eq('role', 'assistant')
        .not('response_time_ms', 'is', null);

      if (error) throw error;

      const times = (data as { response_time_ms: number }[])?.map(d => d.response_time_ms) || [];
      if (times.length === 0) return 0;

      const avg = times.reduce((sum, t) => sum + t, 0) / times.length;
      return Math.round(avg);
    },
  });

  const isLoading = loadingStats || loadingRegistrations || loadingAI || loadingActivity || loadingDAU || loadingAICategories || loadingAIBySSW || loadingAIByHour || loadingResponseTime;

  // Calculate summary stats
  const totalAIQuestions = aiUsageData?.reduce((sum, d) => sum + d.questions, 0) || 0;
  const avgDAU = dauData ? Math.round(dauData.reduce((sum, d) => sum + d.dau, 0) / dauData.length) : 0;
  const topCategory = aiCategoryData?.[0];
  const totalCategorizedMessages = aiCategoryData?.reduce((sum, d) => sum + d.count, 0) || 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Analytics-Übersicht</h1>
        <p className="text-slate-600">Nutzungsstatistiken und Metriken</p>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-slate-500">Lädt Statistiken...</div>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Users className="w-4 h-4 text-teal-600" />
                <span className="text-xs text-slate-500">Nutzer</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {overallStats?.users.toLocaleString('de-DE')}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Baby className="w-4 h-4 text-rose-600" />
                <span className="text-xs text-slate-500">Schwangerschaften</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {overallStats?.pregnancies.toLocaleString('de-DE')}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Heart className="w-4 h-4 text-purple-600" />
                <span className="text-xs text-slate-500">Partner</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {overallStats?.partners.toLocaleString('de-DE')}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-blue-600" />
                <span className="text-xs text-slate-500">Termine</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {overallStats?.appointments.toLocaleString('de-DE')}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Activity className="w-4 h-4 text-amber-600" />
                <span className="text-xs text-slate-500">Ø DAU</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {avgDAU}
              </div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-4 h-4 text-emerald-600" />
                <span className="text-xs text-slate-500">AI-Fragen</span>
              </div>
              <div className="text-2xl font-bold text-slate-900">
                {totalAIQuestions.toLocaleString('de-DE')}
              </div>
            </div>
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* User Registrations */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-5 h-5 text-teal-500" />
                <h2 className="text-lg font-semibold text-slate-900">Neue Registrierungen (30 Tage)</h2>
              </div>
              {registrationData && registrationData.length > 0 ? (
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={registrationData}>
                      <defs>
                        <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#0d9488" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#0d9488" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis />
                      <Tooltip />
                      <Area
                        type="monotone"
                        dataKey="users"
                        stroke="#0d9488"
                        fill="url(#colorUsers)"
                        name="Neue Nutzer"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  Keine Daten vorhanden
                </div>
              )}
            </div>

            {/* Daily Active Users */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-blue-500" />
                <h2 className="text-lg font-semibold text-slate-900">Tägliche aktive Nutzer (30 Tage)</h2>
              </div>
              {dauData && dauData.length > 0 ? (
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dauData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} interval={4} />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="dau"
                        stroke="#3b82f6"
                        strokeWidth={2}
                        dot={false}
                        name="DAU"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  Keine Daten vorhanden
                </div>
              )}
            </div>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* AI Usage */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Bot className="w-5 h-5 text-emerald-500" />
                <h2 className="text-lg font-semibold text-slate-900">AI-Nutzung</h2>
              </div>
              {aiUsageData && aiUsageData.length > 0 ? (
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={aiUsageData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="questions" fill="#10b981" name="Fragen" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  Keine AI-Nutzungsdaten vorhanden
                </div>
              )}
            </div>

            {/* Top Activities */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare className="w-5 h-5 text-purple-500" />
                <h2 className="text-lg font-semibold text-slate-900">Top Aktivitäten (7 Tage)</h2>
              </div>
              {activityData && activityData.length > 0 ? (
                <div className="space-y-3">
                  {activityData.map((item, index) => (
                    <div key={item.action} className="flex items-center gap-3">
                      <div className="w-6 text-center text-sm font-medium text-slate-400">
                        {index + 1}.
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm text-slate-700">{item.action}</span>
                          <span className="text-sm font-medium text-slate-900">
                            {item.count.toLocaleString('de-DE')}
                          </span>
                        </div>
                        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-purple-500 rounded-full"
                            style={{
                              width: `${(item.count / activityData[0].count) * 100}%`,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="h-[250px] flex items-center justify-center text-slate-500">
                  Keine Aktivitätsdaten vorhanden
                </div>
              )}
            </div>
          </div>

          {/* AI Assistant Analytics Section */}
          <div className="mt-10 pt-8 border-t border-slate-200">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-6 h-6 text-emerald-500" />
                <h2 className="text-2xl font-bold text-slate-900">KI-Assistent Analyse</h2>
              </div>
              <p className="text-slate-600">Detaillierte Auswertung der KI-Chat Nutzung</p>
            </div>

            {/* AI Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Bot className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs text-slate-500">Kategorisierte Fragen</span>
                </div>
                <div className="text-2xl font-bold text-slate-900">
                  {totalCategorizedMessages.toLocaleString('de-DE')}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <PieChartIcon className="w-4 h-4 text-purple-600" />
                  <span className="text-xs text-slate-500">Top-Thema</span>
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {topCategory?.label || '-'}
                </div>
                {topCategory && (
                  <div className="text-xs text-slate-400 mt-1">
                    {topCategory.count} Fragen ({totalCategorizedMessages > 0 ? Math.round((topCategory.count / totalCategorizedMessages) * 100) : 0}%)
                  </div>
                )}
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <span className="text-xs text-slate-500">Ø Antwortzeit</span>
                </div>
                <div className="text-2xl font-bold text-slate-900">
                  {avgResponseTime ? `${(avgResponseTime / 1000).toFixed(1)}s` : '-'}
                </div>
              </div>
              <div className="bg-white rounded-lg shadow-sm p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Baby className="w-4 h-4 text-rose-600" />
                  <span className="text-xs text-slate-500">Aktivste SSW</span>
                </div>
                <div className="text-2xl font-bold text-slate-900">
                  {aiBySSWData ? (
                    (() => {
                      const maxSSW = aiBySSWData.reduce((max, d) => d.count > max.count ? d : max, { ssw: 0, count: 0 });
                      return maxSSW.count > 0 ? `SSW ${maxSSW.ssw}` : '-';
                    })()
                  ) : '-'}
                </div>
              </div>
            </div>

            {/* AI Charts Row 1 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Category Distribution Pie Chart */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <PieChartIcon className="w-5 h-5 text-purple-500" />
                  <h3 className="text-lg font-semibold text-slate-900">Themen-Verteilung</h3>
                </div>
                {aiCategoryData && aiCategoryData.length > 0 ? (
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={aiCategoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="count"
                          nameKey="label"
                          label={({ label, percent }) => `${label} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {aiCategoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => value.toLocaleString('de-DE')}
                          labelFormatter={(label) => label}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[300px] flex items-center justify-center text-slate-500">
                    Keine Kategorie-Daten vorhanden
                  </div>
                )}
              </div>

              {/* Top Categories List */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                  <h3 className="text-lg font-semibold text-slate-900">Top-Kategorien</h3>
                </div>
                {aiCategoryData && aiCategoryData.length > 0 ? (
                  <div className="space-y-3">
                    {aiCategoryData.slice(0, 7).map((item, index) => (
                      <div key={item.category} className="flex items-center gap-3">
                        <div className="w-6 text-center text-sm font-medium text-slate-400">
                          {index + 1}.
                        </div>
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <div className="flex-1">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-sm text-slate-700">{item.label}</span>
                            <span className="text-sm font-medium text-slate-900">
                              {item.count.toLocaleString('de-DE')}
                            </span>
                          </div>
                          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${totalCategorizedMessages > 0 ? (item.count / totalCategorizedMessages) * 100 : 0}%`,
                                backgroundColor: item.color,
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-500">
                    Keine Kategorie-Daten vorhanden
                  </div>
                )}
              </div>
            </div>

            {/* AI Charts Row 2 */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Usage by SSW */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Baby className="w-5 h-5 text-rose-500" />
                  <h3 className="text-lg font-semibold text-slate-900">Nutzung nach SSW</h3>
                </div>
                {aiBySSWData && aiBySSWData.some(d => d.count > 0) ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={aiBySSWData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                          dataKey="ssw"
                          tick={{ fontSize: 10 }}
                          interval={3}
                          tickFormatter={(ssw) => `${ssw}`}
                        />
                        <YAxis />
                        <Tooltip
                          labelFormatter={(ssw) => `SSW ${ssw}`}
                          formatter={(value: number) => [value.toLocaleString('de-DE'), 'Fragen']}
                        />
                        <Bar dataKey="count" fill="#f43f5e" name="Fragen" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-500">
                    Keine SSW-Daten vorhanden
                  </div>
                )}
              </div>

              {/* Usage by Hour */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <h3 className="text-lg font-semibold text-slate-900">Nutzung nach Tageszeit</h3>
                </div>
                {aiByHourData && aiByHourData.some(d => d.count > 0) ? (
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={aiByHourData}>
                        <defs>
                          <linearGradient id="colorHour" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="hour" tick={{ fontSize: 10 }} interval={2} />
                        <YAxis />
                        <Tooltip
                          formatter={(value: number) => [value.toLocaleString('de-DE'), 'Fragen']}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#3b82f6"
                          fill="url(#colorHour)"
                          name="Fragen"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[250px] flex items-center justify-center text-slate-500">
                    Keine Tageszeit-Daten vorhanden
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
